export type AutoDispatchMode = 'normal' | 'transfer' | 'timeline' | 'scenario' | 'swap';

export type InitialResponse = {
  kind: 'queued' | 'immediate' | 'failed';
  /** queued のときだけ入る。取れなければ null */
  sessionId: string | null;
  /**
   * scenario の queued 応答が使う識別子（`session_id` を持たない。
   * POST /api/v5/scenario_plannings は `{ status: 'queued', scenario_planning_id, ... }` を返す。
   * queued のときだけ入る。取れなければ null
   */
  scenarioPlanningId: string | null;
  /** 他の識別子と同様、polling の識別に使う（`src/hooks/useQueryPlanningsResults.ts`）。今のところ
   * この controller の 4 モードは生成しないが、応答型 `AsyncPlanningResponseEntity` が持つため素通しする */
  respectRecentId: string | null;
  /**
   * この関数は mode で強制していない（raw にあれば素通しする）。timeline は応答型
   * （`AsyncPlanningResponseEntity`）がそもそもこのフィールドを持たないため実際には常に
   * 空になるだけ（設計書 :697-711）。空を保証しているのは呼び出し側（`completeImmediately`
   * の mode guard）であり、ここではない。この値を他所で直接使うときは注意すること
   */
  notAllocatedOrderIds: number[];
};

export type PollingResult = {
  notAllocatedOrderIds: number[];
  /**
   * 完了時に controller が保持を更新する（設計書 :151）。
   * キーは latest_algorithm_request_version（`planningResponseEntity.ts:54`）。
   * algorithm_request_version と書くと常に null になり、次の自動配車が
   * version 不整合で落ちる。
   */
  latestAlgorithmRequestVersion: number | null;
  /**
   * 割当不可理由の参照に使う session（`planningResponseEntity.ts:48` で必須の
   * `session_id`）。scenario の追跡 token は scenario_planning_id だが、これは
   * 別物 — 混同すると「バッジは出るが理由が空」になる（Task 0）
   */
  sessionId: string | null;
};

type RawResponse = {
  status?: string;
  session_id?: string;
  scenario_planning_id?: string;
  respect_recent_id?: string;
  not_allocated_order_ids?: number[];
};

/**
 * mutate の**初期応答**を正規化する。polling 完了には使わない。
 *
 * Why not scenario の非 queued を immediate 扱いにする: 現行の
 * useScenarioPlanningRequestV5.ts:38 が「シナリオ実行に失敗しました」を出す。
 * 同期完了と同一視すると、走っていない配車を完了として通知してしまう。
 *
 * Why not polling 完了も同じ関数で扱う: scenario は queued のあとの polling 完了が
 * **成功**なので、同じ関数に混ぜると完了を失敗として扱ってしまう。
 *
 * Why `status === 'queued'` だけで queued と判定しない（whole-branch レビュー M-1）:
 * 識別子（session_id / scenario_planning_id / respect_recent_id）が 1 つも無いと、
 * polling の URL（`requestPathV5`）がすべて undefined の空クエリになる。3 つとも
 * 欠けているときは queued として扱わない。
 */
export function normalizeInitialResponse(
  mode: AutoDispatchMode,
  raw: RawResponse,
): InitialResponse {
  const hasQueueIdentifier = raw.session_id != null || raw.scenario_planning_id != null || raw.respect_recent_id != null;
  if (raw.status === 'queued' && hasQueueIdentifier) {
    return {
      kind: 'queued',
      sessionId: raw.session_id ?? null,
      scenarioPlanningId: raw.scenario_planning_id ?? null,
      respectRecentId: raw.respect_recent_id ?? null,
      notAllocatedOrderIds: [],
    };
  }
  if (mode === 'scenario') {
    return {
      kind: 'failed', sessionId: null, scenarioPlanningId: null, respectRecentId: null, notAllocatedOrderIds: [],
    };
  }
  // 即時完了でも session_id は返る（planningResponseEntity.ts:11）。
  // 現行 :2771 が割当不可理由バッジに使うので落とさない
  return {
    kind: 'immediate',
    sessionId: raw.session_id ?? null,
    scenarioPlanningId: null,
    respectRecentId: null,
    notAllocatedOrderIds: raw.not_allocated_order_ids ?? [],
  };
}

/** polling 完了の正規化。モードに依らず成功として扱う */
export function normalizePollingResult(
  raw: { not_allocated_order_ids?: number[]; latest_algorithm_request_version?: number; session_id?: string },
): PollingResult {
  return {
    notAllocatedOrderIds: raw.not_allocated_order_ids ?? [],
    latestAlgorithmRequestVersion: raw.latest_algorithm_request_version ?? null,
    sessionId: raw.session_id ?? null,
  };
}
